import React, { useState } from "react";
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { ChevronDown, ChevronUp, CheckCircle, Settings, Brain, Info, Star, TrendingUp, BarChart3, Activity, User, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";

interface CandidateProfilesTabProps {
  profilesData: any;
}

// Component for showing missing data message
const MissingDataMessage = ({ sectionName }: { sectionName: string }) => {
  const { t } = useTranslation();
  
  return (
    <div className="flex flex-col items-center justify-center p-12 text-center bg-gradient-to-br from-slate-50 to-slate-100 rounded-sm border border-slate-200">
      <div className="p-4 rounded-sm bg-slate-200 mb-6">
        <Settings className="h-8 w-8 text-slate-500" />
      </div>
      <h3 className="text-xl text-slate-800 mb-3">{t('candidates.science_analysis.analysisPending')}</h3>
      <p className="text-sm text-slate-600 max-w-md leading-relaxed mb-4">
        {t('candidates.science_analysis.analysisHasntCompleted', { sectionName })}
      </p>
    </div>
  );
};

// Helper function to check if data exists and has meaningful content
const hasValidData = (data: any): boolean => {
  if (!data || typeof data !== 'object') return false;
  
  // Check if it's an empty object
  if (Object.keys(data).length === 0) return false;
  
  // Check if all values are null, undefined, or empty
  const values = Object.values(data);
  return values.some(value => {
    if (value === null || value === undefined) return false;
    if (typeof value === 'string' && value.trim() === '') return false;
    if (typeof value === 'object' && Object.keys(value).length === 0) return false;
    if (Array.isArray(value) && value.length === 0) return false;
    return true;
  });
};

// DISC standard colors: Dominance (Red), Influence (Yellow), Steadiness (Green), Conscientiousness (Blue)
const discColors = ["#dc2626", "#eab308", "#16a34a", "#2563eb"];
const traitColors = [...discColors, "#64748b"]; // Add neutral gray for other traits

// MBTI dimension colors for visual distinction
const mbtiDimensionColors: Record<string, { bg: string; text: string; border: string }> = {
  "Energy": { bg: "bg-blue-50", text: "text-blue-700", border: "border-blue-200" },
  "Information": { bg: "bg-green-50", text: "text-green-700", border: "border-green-200" },
  "Decisions": { bg: "bg-rose-50", text: "text-rose-700", border: "border-rose-200" },
  "Lifestyle": { bg: "bg-amber-50", text: "text-amber-700", border: "border-amber-200" },
};

const getMbtiDimensionColor = (dimension: string) => {
  if (dimension.includes("Energy") || dimension.includes("E/I") || dimension.includes("Extraversion") || dimension.includes("Introversion")) {
    return mbtiDimensionColors["Energy"];
  }
  if (dimension.includes("Information") || dimension.includes("S/N") || dimension.includes("Sensing") || dimension.includes("Intuition")) {
    return mbtiDimensionColors["Information"];
  }
  if (dimension.includes("Decision") || dimension.includes("T/F") || dimension.includes("Thinking") || dimension.includes("Feeling")) {
    return mbtiDimensionColors["Decisions"];
  }
  if (dimension.includes("Lifestyle") || dimension.includes("J/P") || dimension.includes("Judging") || dimension.includes("Perceiving")) {
    return mbtiDimensionColors["Lifestyle"];
  }
  return { bg: "bg-blue-50", text: "text-blue-700", border: "border-blue-200" }; // Default
};

const SectionCard = ({ title, description, icon, children }: { 
  title: string; 
  description?: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
}) => (
  <Card className="mb-8 shadow-sm border border-slate-200 bg-white dark:bg-gray-800">
    <CardHeader className="pb-4">
      <div className="flex items-center gap-3">
        {icon && (
          <div className="p-2 rounded-sm bg-blue-50 text-blue-500">
            {icon}
          </div>
        )}
        <div>
          <CardTitle className="text-xl text-slate-800">{title}</CardTitle>
          {description && (
            <CardDescription className="text-sm text-slate-600 mt-1">
              {description}
            </CardDescription>
          )}
        </div>
      </div>
    </CardHeader>
    <CardContent className="pt-0">{children}</CardContent>
  </Card>
);

// Professional ExpandableList component
const ExpandableList = ({ items, label, open, onClick }: { 
  items: string[]; 
  label: string; 
  open: boolean; 
  onClick: () => void;
}) => {
  const { t } = useTranslation();
  
  if (!items || items.length === 0) return null;
  
  return (
    <div className="mb-4">
      <Button
        variant="outline"
        onClick={onClick}
        className="w-full justify-between text-left h-auto p-3 bg-slate-50 hover:bg-slate-100 border-slate-200"
      >
        <span className="flex items-center gap-2 text-sm font-medium">
          {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          {open ? `${t('candidates.science_analysis.hide')} ${label}` : `${t('candidates.science_analysis.show')} ${label}`} ({items.length})
        </span>
      </Button>
      
      {open && (
        <div className="mt-3 p-4 bg-slate-50 rounded-sm border border-slate-200">
          <div className="grid gap-2">
            {items.map((item, index) => (
              <div key={index} className="flex items-start gap-3 p-2 bg-white dark:bg-gray-800 rounded-sm">
                <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                <span className="text-sm text-slate-700 leading-relaxed">{item}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export const CandidateProfilesTab: React.FC<CandidateProfilesTabProps> = ({ profilesData }) => {
  const { t } = useTranslation();
  
  // Check if we have any valid profile data
  if (!profilesData || !hasValidData(profilesData)) {
    return (
      <div className="space-y-6">
        <MissingDataMessage sectionName={t('candidates.science_analysis.personalityAnalysis')} />
      </div>
    );
  }

  // DISC
  const discTraits = ["Dominance", "Influence", "Steadiness", "Conscientiousness"];
  const hasDiscData = hasValidData(profilesData.DISC);
  const discData = hasDiscData ? discTraits.map((trait, i) => ({
    trait,
    value: profilesData.DISC?.[trait]?.level === "High" ? 100 : profilesData.DISC?.[trait]?.level === "Moderate" ? 60 : 30,
    evidence: profilesData.DISC?.[trait]?.evidence || [],
    fill: traitColors[i % traitColors.length],
  })) : [];
  const [openDiscIndex, setOpenDiscIndex] = useState<number | null>(null);

  // Big Five
  const bigFiveTraits = ["Openness", "Conscientiousness", "Extraversion", "Agreeableness", "Neuroticism"];
  const hasBigFiveData = hasValidData(profilesData.BigFive);
  const bigFiveData = hasBigFiveData ? bigFiveTraits.map((trait, i) => {
    const score = profilesData.BigFive?.[trait]?.score || "";
    let value = 50;
    if (typeof score === "string") {
      if (score.includes("High")) value = 90;
      else if (score.includes("Moderate")) value = 60;
      else if (score.includes("Low")) value = 30;
    }
    return {
      trait,
      value,
      indicators: profilesData.BigFive?.[trait]?.indicators || [],
      fill: traitColors[i % traitColors.length],
    };
  }) : [];
  const [openBigFiveIndex, setOpenBigFiveIndex] = useState<number | null>(null);

  // MBTI
  const mbti = profilesData.MBTI || {};
  const hasMbtiData = hasValidData(mbti);
  const mbtiBreakdown = mbti.dimension_breakdown || {};

  // Observations
  const obs = profilesData.observations || {};
  const hasObservationsData = hasValidData(obs);

  return (
    <div className="space-y-8 max-w-7xl mx-auto">
      {/* Analysis Summary Header */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-sm p-6 border border-blue-200">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-blue-100 rounded-sm">
            <Brain className="h-8 w-8 text-blue-500" />
          </div>
          <div className="flex-1">
            <h2 className="text-xl text-slate-800 mb-2">{t('candidates.science_analysis.personalityAnalysis')}</h2>
            <p className="text-slate-600">
              {t('candidates.science_analysis.aiPoweredProfiling')}
            </p>
          </div>
          {hasValidData(profilesData.summary) && (
            <div className="text-right">
              <Badge variant="secondary" className="mb-2">
                <Star className="h-3 w-3 mr-1" />
                {profilesData.summary?.confidence_level || t('candidates.science_analysis.naValue')}
              </Badge>
              <p className="text-xs text-slate-500">{t('candidates.science_analysis.confidence')}</p>
            </div>
          )}
        </div>
      </div>

      {/* Summary Card */}
      {hasValidData(profilesData.summary) && (
        <SectionCard 
          title={t('candidates.science_analysis.analysisSummary')} 
          description={t('candidates.science_analysis.qualityAssessment')}
          icon={<Info className="h-5 w-5" />}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="p-4 bg-blue-50 rounded-sm border border-blue-200">
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp className="h-4 w-4 text-blue-500" />
                <span className="font-medium text-blue-800">{t('candidates.science_analysis.confidenceLevel')}</span>
              </div>
              <p className="font-medium text-blue-900">
                {profilesData.summary?.confidence_level}
              </p>
            </div>
            <div className="p-4 bg-green-50 rounded-sm border border-green-200">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle className="h-4 w-4 text-green-600" />
                <span className="font-medium text-green-800">{t('candidates.science_analysis.dataQuality')}</span>
              </div>
              <p className="text-sm text-green-900">
                {profilesData.summary?.data_quality_notes}
              </p>
            </div>
          </div>
        </SectionCard>
      )}

      {/* DISC Profile */}
      <SectionCard 
        title={t('candidates.science_analysis.discProfile')} 
        description={t('candidates.science_analysis.discDescription')}
        icon={<BarChart3 className="h-5 w-5" />}
      >
        {hasDiscData ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="space-y-4">
              {/* DISC Color Legend */}
              <div className="grid grid-cols-2 gap-2 mb-3 p-3 bg-slate-50 rounded-sm border border-slate-200">
                {discData.map((item, idx) => (
                  <div key={item.trait} className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.fill }}></div>
                    <span className="text-xs font-medium text-slate-600">{item.trait}</span>
                  </div>
                ))}
              </div>
              <h4 className="font-medium text-slate-700 mb-4">{t('candidates.science_analysis.traitDistribution')}</h4>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={discData} layout="vertical" margin={{ left: 60, right: 30, top: 20, bottom: 20 }}>
                    <XAxis type="number" domain={[0, 100]} axisLine={false} tickLine={false} />
                    <YAxis type="category" dataKey="trait" width={80} axisLine={false} tickLine={false} />
                    <Tooltip 
                      formatter={(value: number) => [`${value}%`, t('candidates.science_analysis.score')]}
                      contentStyle={{ 
                        backgroundColor: 'white', 
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px',
                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                      }}
                    />
                    <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                      {discData.map((entry, idx) => (
                        <Cell key={`cell-${idx}`} fill={entry.fill} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
            
            <div className="space-y-4">
              <h4 className="font-medium text-slate-700 mb-4">{t('candidates.science_analysis.supportingEvidence')}</h4>
              <div className="space-y-3">
                {discData.map((trait, index) => (
                  <ExpandableList
                    key={trait.trait}
                    items={trait.evidence}
                    label={t('candidates.science_analysis.evidenceFor', { trait: trait.trait })}
                    open={openDiscIndex === index}
                    onClick={() => setOpenDiscIndex(openDiscIndex === index ? null : index)}
                  />
                ))}
              </div>
            </div>
          </div>
        ) : (
          <MissingDataMessage sectionName={t('candidates.science_analysis.discProfile')} />
        )}
      </SectionCard>

      {/* Big Five Profile */}
      <SectionCard 
        title={t('candidates.science_analysis.bigFiveTraits')} 
        description={t('candidates.science_analysis.bigFiveDescription')}
        icon={<Activity className="h-5 w-5" />}
      >
        {hasBigFiveData ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="space-y-4">
              <h4 className="font-medium text-slate-700 mb-4">{t('candidates.science_analysis.personalityRadar')}</h4>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <RadarChart data={bigFiveData} margin={{ top: 20, right: 30, bottom: 20, left: 30 }}>
                    <PolarGrid gridType="polygon" />
                    <PolarAngleAxis dataKey="trait" tick={{ fontSize: 12 }} />
                    <PolarRadiusAxis 
                      angle={90} 
                      domain={[0, 100]} 
                      tick={false}
                      axisLine={false}
                    />
                    <Radar 
                      name={t('candidates.science_analysis.personalityTraits')} 
                      dataKey="value" 
                      stroke="#2563eb" 
                      fill="#2563eb" 
                      fillOpacity={0.3}
                      strokeWidth={2}
                    />
                    <Tooltip 
                      formatter={(value: number) => [`${value}%`, t('candidates.science_analysis.score')]}
                      contentStyle={{ 
                        backgroundColor: 'white', 
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px',
                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                      }}
                    />
                  </RadarChart>
                </ResponsiveContainer>
              </div>
            </div>
            
            <div className="space-y-4">
              <h4 className="font-medium text-slate-700 mb-4">{t('candidates.science_analysis.behavioralIndicators')}</h4>
              <div className="space-y-3">
                {bigFiveData.map((trait, index) => (
                  <ExpandableList
                    key={trait.trait}
                    items={trait.indicators}
                    label={t('candidates.science_analysis.indicatorsFor', { trait: trait.trait })}
                    open={openBigFiveIndex === index}
                    onClick={() => setOpenBigFiveIndex(openBigFiveIndex === index ? null : index)}
                  />
                ))}
              </div>
            </div>
          </div>
        ) : (
          <MissingDataMessage sectionName={t('candidates.science_analysis.bigFiveTraits')} />
        )}
      </SectionCard>

      {/* MBTI Profile */}
      <SectionCard 
        title={t('candidates.science_analysis.mbtiPersonalityType')} 
        description={t('candidates.science_analysis.mbtiDescription')}
        icon={<User className="h-5 w-5" />}
      >
        {hasMbtiData ? (
          <div className="space-y-6">
            <div className="flex items-center justify-between p-6 bg-gradient-to-r from-purple-50 to-pink-50 rounded-sm border border-purple-200">
              <div>
                <h4 className="font-medium text-purple-900 mb-2">{t('candidates.science_analysis.estimatedType')}</h4>
                <div className="flex items-center gap-4">
                  <span className="text-3xl font-bold text-purple-700">{mbti.estimated_type}</span>
                  <Badge variant="secondary" className="bg-purple-100 text-purple-800">
                    {mbti.confidence} {t('candidates.science_analysis.confidence').toLowerCase()}
                  </Badge>
                </div>
              </div>
            </div>
            
            <div className="overflow-hidden rounded-sm border border-slate-200">
              <table className="w-full">                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-6 py-4 text-left text-sm font-medium text-slate-700">{t('candidates.science_analysis.dimension')}</th>
                      <th className="px-6 py-4 text-left text-sm font-medium text-slate-700">{t('candidates.science_analysis.preference')}</th>
                      <th className="px-6 py-4 text-left text-sm font-medium text-slate-700">{t('candidates.science_analysis.justification')}</th>
                    </tr>
                  </thead>
                <tbody className="divide-y divide-slate-200">
                  {Object.entries(mbtiBreakdown).map(([dimension, details]: any, index) => {
                    const colors = getMbtiDimensionColor(dimension);
                    return (
                      <tr key={index} className="hover:bg-slate-50 transition-colors">
                        <td className="px-6 py-4 font-medium text-slate-800">{dimension}</td>
                        <td className="px-6 py-4">
                          <Badge variant="outline" className={`${colors.bg} ${colors.text} ${colors.border}`}>
                            {details.estimated}
                          </Badge>
                        </td>
                        <td className="px-6 py-4 text-sm text-slate-600 leading-relaxed">
                          {Array.isArray(details.justification) 
                            ? details.justification.join(". ") 
                            : details.justification}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <MissingDataMessage sectionName={t('candidates.science_analysis.mbtiPersonalityType')} />
        )}
      </SectionCard>

      {/* Observations and Insights */}
      <SectionCard 
        title={t('candidates.science_analysis.professionalInsights')} 
        description={t('candidates.science_analysis.insightsDescription')}
        icon={<Star className="h-5 w-5" />}
      >
        {hasObservationsData ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="p-4 bg-white dark:bg-gray-800 rounded-sm border border-green-200">
                <h4 className="font-medium text-green-800 mb-3 flex items-center gap-2">
                  <CheckCircle className="h-4 w-4" />
                  {t('candidates.science_analysis.keyStrengths')}
                </h4>
                <div className="space-y-2">
                  {obs.strengths?.map((strength: string, index: number) => (
                    <div key={index} className="flex items-start gap-2 p-2 bg-green-50 rounded-sm">
                      <div className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></div>
                      <span className="text-sm text-slate-700">{strength}</span>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="p-4 bg-white dark:bg-gray-800 rounded-sm border border-orange-200">
                <h4 className="font-medium text-orange-800 mb-3 flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4" />
                  {t('candidates.science_analysis.growthAreas')}
                </h4>
                <div className="space-y-2">
                  {obs.potential_challenges?.map((challenge: string, index: number) => (
                    <div key={index} className="flex items-start gap-2 p-2 bg-orange-50 rounded-sm">
                      <div className="w-2 h-2 bg-orange-500 rounded-full mt-2 flex-shrink-0"></div>
                      <span className="text-sm text-slate-700">{challenge}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            
            <div className="p-4 bg-white dark:bg-gray-800 rounded-sm border border-blue-200">
              <h4 className="font-medium text-blue-800 mb-3 flex items-center gap-2">
                <Info className="h-4 w-4" />
                {t('candidates.science_analysis.contextualNotes')}
              </h4>
              <p className="text-sm text-slate-700 leading-relaxed">{obs.contextual_notes}</p>
            </div>
          </div>
        ) : (
          <MissingDataMessage sectionName={t('candidates.science_analysis.professionalInsights')} />
        )}
      </SectionCard>
    </div>
  );
}; 